import React from 'react'
import { formatMembershipDate, type MembershipProfile } from '../../lib/membership'
import type { PaidMembershipPlan } from '../../lib/billingCatalog'
import { VoxnelaAlertModal } from '../ui/VoxnelaAlertModal'
import { useUiText } from '../../i18n/ui'
import { formatPaywallMessage, paywallMessages } from '../../i18n/paywall'

export interface QuotaExhaustedModalProps {
  open: boolean
  onClose: () => void
  membership: MembershipProfile
  onOpenPaywall: (options?: { initialPlan?: PaidMembershipPlan }) => void
}

export const QuotaExhaustedModal: React.FC<QuotaExhaustedModalProps> = ({
  open,
  onClose,
  membership,
  onOpenPaywall,
}) => {
  const { locale } = useUiText()
  const copy = paywallMessages[locale]
  const plan = membership.plan
  const renewalDateText = formatMembershipDate(membership.renewalAtMs, locale)

  const handlePrimaryAction = () => {
    if (plan === 'free') {
      onClose()
      onOpenPaywall()
    } else if (plan === 'plus' || plan === 'pro') {
      onClose()
      onOpenPaywall({ initialPlan: 'studio' })
    } else {
      onClose()
    }
  }

  const content = plan === 'free'
    ? {
        title: copy.quotaFreeTitle,
        message: copy.quotaFreeMessage,
        primaryLabel: copy.upgrade,
        secondaryLabel: copy.later,
        footer: copy.currentCredit,
      }
    : plan === 'plus' || plan === 'pro'
      ? {
          title: copy.quotaProTitle,
          message: copy.quotaProMessage,
          primaryLabel: copy.upgradeStudio,
          secondaryLabel: copy.later,
          footer: formatPaywallMessage(copy.renewalDate, { date: renewalDateText }),
        }
      : {
          title: copy.quotaStudioTitle,
          message: formatPaywallMessage(copy.quotaStudioMessage, { date: renewalDateText }),
          primaryLabel: copy.done,
          secondaryLabel: undefined,
          footer: `${copy.currentCredit} · ${formatPaywallMessage(copy.renewalDate, { date: renewalDateText })}`,
        }

  return (
    <VoxnelaAlertModal
      open={open}
      title={content.title}
      message={content.message}
      primaryLabel={content.primaryLabel}
      secondaryLabel={content.secondaryLabel}
      onPrimary={handlePrimaryAction}
      onSecondary={content.secondaryLabel ? onClose : undefined}
      onClose={onClose}
      footer={<p>{content.footer}</p>}
    />
  )
}
